import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface LazyVideoProps {
  src: string;
  poster?: string;
  className?: string;
  autoPlay?: boolean;
  loop?: boolean;
}

export default function LazyVideo({
  src,
  poster,
  className = '',
  autoPlay = true,
  loop = true,
}: LazyVideoProps) {
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const fullscreenVideoRef = useRef<HTMLVideoElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px', threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Handle video loaded
  const handleLoadedData = () => {
    setIsLoaded(true);
  };

  // Auto-play preview when loaded (muted)
  useEffect(() => {
    if (isLoaded && autoPlay && previewVideoRef.current) {
      previewVideoRef.current.play().catch(() => {});
    }
  }, [isLoaded, autoPlay]);

  // Open fullscreen modal
  const openFullscreen = () => {
    setIsFullscreen(true);
    document.body.style.overflow = 'hidden';
    // Sync time from preview to fullscreen
    if (previewVideoRef.current) {
      previewVideoRef.current.pause();
    }
  };

  // Close fullscreen modal
  const closeFullscreen = () => {
    setIsFullscreen(false);
    document.body.style.overflow = '';
    if (fullscreenVideoRef.current) {
      fullscreenVideoRef.current.pause();
    }
    // Resume preview
    if (previewVideoRef.current) {
      previewVideoRef.current.play().catch(() => {});
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        closeFullscreen();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isFullscreen]);

  // Play fullscreen video when modal opens
  useEffect(() => {
    if (isFullscreen && fullscreenVideoRef.current) {
      fullscreenVideoRef.current.currentTime = 0;
      fullscreenVideoRef.current.play().catch(() => {});
    }
  }, [isFullscreen]);

  return (
    <>
      {/* Preview container */}
      <div
        ref={containerRef}
        className={`relative overflow-hidden cursor-pointer group ${className}`}
        onClick={openFullscreen}
      >
        {/* Loading skeleton */}
        {!isLoaded && (
          <div className="absolute inset-0 bg-[#0a0a0a] flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 rounded-full border border-white/10 animate-ping" style={{ animationDuration: '2s' }} />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full border border-white/20 animate-pulse" />
              </div>
              <div className="w-16 h-16 rounded-full border-2 border-white/30 flex items-center justify-center">
                <div className="w-2 h-2 rounded-full bg-white/50" />
              </div>
            </div>
            <div className="absolute bottom-6 left-0 right-0 text-center">
              <span className="text-[10px] tracking-[0.3em] uppercase text-white/30">Loading</span>
            </div>
          </div>
        )}

        {/* Preview video - muted, looping */}
        {isInView && (
          <video
            ref={previewVideoRef}
            src={src}
            poster={poster}
            muted
            loop={loop}
            playsInline
            preload="metadata"
            onLoadedData={handleLoadedData}
            className={`w-full h-full object-cover transition-opacity duration-700 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        )}

        {/* Hover overlay - only visible on hover */}
        {isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-transparent group-hover:bg-black/50 transition-all duration-300">
            {/* Play button - hidden by default, appears on hover */}
            <div className="transform transition-all duration-300 opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100">
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center hover:bg-white/30 transition-all">
                <svg className="w-6 h-6 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>

            {/* Watch full video hint */}
            <div className="absolute bottom-4 left-0 right-0 text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
              <span className="text-[10px] tracking-[0.2em] uppercase text-white/80 bg-black/60 px-3 py-1.5 rounded-full">
                Click to expand
              </span>
            </div>
          </div>
        )}

        {/* Subtle always-visible expand hint in corner */}
        {isLoaded && (
          <div className="absolute bottom-2 right-2 pointer-events-none group-hover:opacity-0 transition-opacity duration-300">
            <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur-sm rounded px-2 py-1 border border-white/10">
              {/* Expand icon */}
              <svg className="w-3 h-3 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              <span className="text-[9px] tracking-wider uppercase text-white/60">Expand</span>
            </div>
          </div>
        )}

        {/* Corner decorations */}
        <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-white/20 pointer-events-none" />
        <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-white/20 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-white/20 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-white/20 pointer-events-none" />
      </div>

      {/* Fullscreen modal - rendered via portal to escape stacking contexts */}
      {isFullscreen && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black flex items-center justify-center animate-fade-in"
          onClick={closeFullscreen}
        >
          {/* Close button */}
          <button
            onClick={closeFullscreen}
            className="absolute top-6 right-6 z-10 w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* ESC hint */}
          <div className="absolute top-6 left-6 text-[10px] tracking-[0.2em] uppercase text-white/30">
            Press ESC to close
          </div>

          {/* Video container */}
          <div
            className="relative w-full max-w-6xl mx-4 aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              ref={fullscreenVideoRef}
              src={src}
              controls
              autoPlay
              loop={loop}
              playsInline
              className="w-full h-full rounded-lg"
            />

            {/* Corner decorations */}
            <div className="absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-white/30 pointer-events-none" />
            <div className="absolute -top-2 -right-2 w-8 h-8 border-t-2 border-r-2 border-white/30 pointer-events-none" />
            <div className="absolute -bottom-2 -left-2 w-8 h-8 border-b-2 border-l-2 border-white/30 pointer-events-none" />
            <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-white/30 pointer-events-none" />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
